import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { buildExpressQuery, buildLoopbackQuery } from '../../repository/queries/builders';
import useExpress, { expressMutation } from '../../repository/react-query/useExpress';
import { editUser } from '../../repository';

const baseEndpoint = '/users';

const getUsersQuery = ({ path, includeDeleted, where, ...rest }) => {
    const params = {
        where: {
            ...(includeDeleted ? { deleted_at: 'any' } : { deleted_at: { eq: null } }),
            ...where,
        },
        ...rest,
    };

    const endpoint = path ? `${baseEndpoint}/${path}` : baseEndpoint;
    return useExpress({ endpoint, params });
};

export const useFetchUsers = ({
    enabled = true,
    key = 'user-data',
    useQueryOptions = {},
    path = '',
    includeDeleted = false,
    ...queryParams // wrap
} = {}) => {
    const queryFn = getUsersQuery({ path, includeDeleted, ...queryParams });
    const queryKey = [key, { includeDeleted, ...queryParams }];
    return useQuery({ queryKey, queryFn, enabled, ...useQueryOptions });
};

export const useUpdateUser = ({ key, expressRequired = false }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return editUser(data, id, expressRequired);
        },
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

const deleteUserQuery = (id) => {
    const endpoint = `${baseEndpoint}/${id}`;
    return buildExpressQuery(endpoint, {});
};

export const useDeleteUser = (key) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id) => {
            const query = deleteUserQuery(id);
            const response = await axios.delete(query);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [key] });
        },
    });
};

const restoreUserQuery = (id) => {
    const endpoint = `${baseEndpoint}/restore-user/${id}`;
    return buildExpressQuery(endpoint, {});
};

export const useRestoreUser = (key) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id) => {
            const query = restoreUserQuery(id);
            const response = await axios.patch(query);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [key] });
        },
    });
};

export const useFetchSessionRecordingUsers = () => {
    const queryFn = useExpress({ endpoint: '/users/session-recording', params: {} });
    return useQuery({
        queryKey: ['session-recording-users'],
        queryFn,
        refetchOnMount: 'always',
    });
};

export const useUpdateSessionRecording = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, until }) =>
            expressMutation({
                method: 'PATCH',
                endpoint: `/users/${id}/session-recording`,
                body: { until: until ?? null },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['session-recording-users'] });
        },
    });
};

export const useBulkUpdateSessionRecording = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userIds, until }) =>
            expressMutation({
                method: 'PATCH',
                endpoint: '/users/session-recording/bulk',
                body: { userIds, until: until ?? null },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['session-recording-users'] });
        },
    });
};
